import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserRoundPlus } from 'lucide-react';
import { publicPost } from '@/lib/public-api';
import { describeError } from '@/lib/auth';
import { useTranslation } from '@/lib/i18n';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { EmptyState } from '@/components/ui/layout';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Button, DatePicker, Field, FormSkeleton, Input } from '@/components/ui/primitives';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { usePublicAuth } from './PublicLayout';

export function AddPersonPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation('portal');
  const { account, loading, requireSignIn } = usePublicAuth();
  const returnTo = safeReturnTo(params.get('returnTo'), '/find/me');

  const [fullName, setFullName] = useState('');
  const [sex, setSex] = useState('unknown');
  const [dob, setDob] = useState('');
  const [relation, setRelation] = useState('child');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => publicPost('/me/people', { fullName: fullName.trim(), sex, dob: dob || undefined, relation }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['public-people'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  if (loading) return <FormSkeleton />;
  if (!account) {
    return (
      <EmptyState
        title="Sign in to add a person"
        description="People you book for are tied to your phone number."
        action={<Button onClick={requireSignIn}>Sign in</Button>}
      />
    );
  }

  const ready = fullName.trim().length >= 2;
  const dirty = !save.isSuccess && (fullName !== '' || sex !== 'unknown' || dob !== '' || relation !== 'child');

  return (
    <FormScreen
      title="Add a person to your number"
      description="They can then be booked for at any hospital, lab or pharmacy here"
      icon={UserRoundPlus}
      eyebrow={t('screen.find_care')}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={t('section.person')} icon={UserRoundPlus}>
        <Field label="Full name" required className="sm:col-span-2">
          <Input value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </Field>
        <Field label="Sex">
          <ChoiceButtons
            layout="chips"
            value={sex}
            onChange={setSex}
            options={[
              { value: 'unknown', label: 'Not said' },
              { value: 'male', label: 'Male' },
              { value: 'female', label: 'Female' },
              { value: 'other', label: 'Other' },
            ]}
          />
        </Field>
        <Field label="Date of birth">
          <DatePicker value={dob} onChange={setDob} />
        </Field>
        <Field label="Relation to you" className="sm:col-span-2">
          <ChoiceButtons
            layout="chips"
            value={relation}
            onChange={setRelation}
            options={[
              { value: 'child', label: 'Child' },
              { value: 'spouse', label: 'Spouse' },
              { value: 'parent', label: 'Parent' },
              { value: 'sibling', label: 'Sibling' },
              { value: 'other', label: 'Other' },
            ]}
          />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Add" loading={save.isPending} incomplete={!ready} cancelTo={returnTo} bleed={false} />
    </FormScreen>
  );
}
